import { apiErrorMessage } from '@agos/api-client'
import type { Category, Product } from '@agos/api-client/model'
import { T, formatCount, tt } from '@agos/shared'
import { BUTTON_PRIMARY, ConfirmDialog, cn, toast } from '@agos/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Copy } from 'lucide-react'
import { useMemo, useState } from 'react'

import { ProductChecklist } from './ProductChecklist'
import {
  copyUpsells,
  refreshUpsellReads,
  type CopyMode,
  type UpsellCopyPayload,
  type UpsellGroup,
  type UpsellItem,
} from './upsell-api'
import {
  checklistGroups,
  copyLines,
  copyPayloadOf,
  copySummary,
  linksOfProduct,
} from './upsell-rules'

const NO_EXCLUSIONS: ReadonlySet<number> = new Set()

/**
 * *Kopírovať doplnky* - one product's upsells onto many - `FRONTEND.md` §9.711.
 *
 * **It says what it will touch before it touches it**, counted from the read this screen already holds: how many
 * products, how many links each gains and, under *nahradiť*, how many of their own it loses first. A copy over a
 * whole category is one press and the confirmation is the only place that number is seen before it is real.
 */
export function CopyPanel({
  items,
  groups,
  products,
  categories,
}: {
  items: readonly UpsellItem[]
  groups: readonly UpsellGroup[]
  products: readonly Product[]
  categories: readonly Category[]
}) {
  const queryClient = useQueryClient()
  const [source, setSource] = useState('')
  const [targets, setTargets] = useState<Set<number>>(new Set())
  const [mode, setMode] = useState<CopyMode>('merge')
  const [confirming, setConfirming] = useState(false)

  const sourceId = source === '' ? null : Number(source)
  const sources = useMemo(() => checklistGroups(products, categories, '', NO_EXCLUSIONS), [products, categories])
  const excluded = useMemo(() => new Set(sourceId === null ? [] : [sourceId]), [sourceId])
  const sourceLinks = sourceId === null ? [] : linksOfProduct(items, sourceId)
  const summary = sourceId === null ? null : copySummary(items, sourceId, targets, mode)

  const nameOf = (id: number) => products.find((product) => product.id === id)?.name ?? `#${String(id)}`
  const groupName = (id: number | null) =>
    id === null ? tt('bez skupiny') : (groups.find((group) => group.id === id)?.name ?? `#${String(id)}`)

  const copy = useMutation({
    mutationFn: (payload: UpsellCopyPayload) => copyUpsells(payload),
    onSuccess: async () => {
      setConfirming(false)
      setTargets(new Set())
      toast({ title: tt('Skopírované'), description: tt('Doplnky sa priradili vybraným produktom.') })
      await refreshUpsellReads(queryClient)
    },
    onError: (caught) =>
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Doplnky sa nepodarilo skopírovať.')),
      }),
  })

  return (
    <div className="space-y-4" data-upsell-copy>
      <label className="block space-y-1 text-sm">
        <span className="font-semibold">
          <T>Kopírovať z produktu</T>
        </span>
        <select
          value={source}
          onChange={(event) => setSource(event.target.value)}
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
          data-upsell-copy-source
        >
          <option value="">{tt('Vyberte produkt...')}</option>
          {sources.map((group) => (
            <optgroup key={group.key} label={tt(group.category)}>
              {group.products.map((product) => (
                <option key={product.id} value={String(product.id)}>
                  {tt(product.name)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>

      {sourceId !== null && (
        <div className="rounded-xl border border-border p-3 text-sm" data-upsell-copy-links={sourceLinks.length}>
          <p className="font-semibold">
            <T>Doplnky zdroja</T>: {formatCount(sourceLinks.length)}
          </p>
          {sourceLinks.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              <T>Tento produkt nemá žiadne doplnky - pridať nie je čo, nahradiť by ich cieľom len zmazalo.</T>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {sourceLinks
                .map((link) => `${tt(nameOf(link.addonProductId))} (${groupName(link.groupId)})`)
                .join(', ')}
            </p>
          )}
        </div>
      )}

      {sourceId !== null && (
        <>
          <fieldset className="flex flex-wrap gap-4 text-sm" data-upsell-copy-mode={mode}>
            <legend className="sr-only">{tt('Spôsob kopírovania')}</legend>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="upsell-copy-mode"
                checked={mode === 'merge'}
                onChange={() => setMode('merge')}
                data-upsell-copy-merge
              />
              <span>
                <b>
                  <T>pridať</T>
                </b>{' '}
                - <T>doplnky zdroja sa pridajú k tým, ktoré cieľ už má</T>
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="upsell-copy-mode"
                checked={mode === 'replace'}
                onChange={() => setMode('replace')}
                data-upsell-copy-replace
              />
              <span>
                <b>
                  <T>nahradiť</T>
                </b>{' '}
                - <T>doplnky cieľa sa najprv zmažú</T>
              </span>
            </label>
          </fieldset>

          <div className="space-y-1">
            <p className="text-sm font-semibold">
              <T>Na produkty</T>
            </p>
            <ProductChecklist
              products={products}
              categories={categories}
              excluded={excluded}
              selected={targets}
              onChange={setTargets}
              marker="upsell-copy"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              disabled={targets.size === 0 || copy.isPending}
              onClick={() => setConfirming(true)}
              className={cn('inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm', BUTTON_PRIMARY)}
              data-upsell-copy-go
            >
              <Copy className="w-4 h-4" />
              <T>Kopírovať doplnky</T>
            </button>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirming && sourceId !== null && summary !== null}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          if (sourceId !== null) copy.mutate(copyPayloadOf(sourceId, targets, mode))
        }}
        title={mode === 'replace' ? tt('Nahradiť doplnky?') : tt('Pridať doplnky?')}
        description={`${tt('Zdroj')}: ${tt(sourceId === null ? '' : nameOf(sourceId))}`}
        confirmLabel={mode === 'replace' ? tt('Nahradiť') : tt('Pridať')}
        variant={mode === 'replace' ? 'danger' : 'default'}
        loading={copy.isPending}
      >
        {summary !== null && (
          <ul
            className="space-y-0.5 text-sm"
            data-upsell-copy-summary
            data-upsell-copy-products={summary.products}
            data-upsell-copy-added={summary.added}
            data-upsell-copy-removed={summary.removed}
          >
            {copyLines(summary, mode).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </ConfirmDialog>
    </div>
  )
}
